import { useState, useMemo } from "react";
import type { HaulierLineItem, WarehouseLineItem } from "../types";
import { InvoiceType } from "../types";

// ─── Types ──────────────────────────────────────────────────────────────────

type SortDir = "asc" | "desc";

interface ColumnDef<T> {
  key: string;
  label: string;
  accessor: (row: T) => string | number;
  /** Right-align numeric columns */
  numeric?: boolean;
  /** Optional custom cell renderer */
  renderCell?: (row: T) => React.ReactNode;
}

type Filter = "ALL" | "ISSUES" | "CORRECT" | "DUPLICATE" | "RATE_NOT_FOUND" | "RETURNS";

interface LineItemsTableProps {
  invoiceType: InvoiceType;
  haulierLines: HaulierLineItem[];
  warehouseLines: WarehouseLineItem[];
  /** When true, show returns filter and highlight return rows */
  showReturns?: boolean;
}

// ─── Status badges ──────────────────────────────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  CORRECT: "bg-green-100 text-green-800",
  OVERCHARGED: "bg-red-100 text-red-800",
  UNDERCHARGED: "bg-amber-100 text-amber-800",
  RATE_NOT_FOUND: "bg-gray-100 text-gray-800",
  UNKNOWN_SERVICE: "bg-gray-100 text-gray-800",
  DUPLICATE: "bg-purple-100 text-purple-800",
};

function StatusBadge({ status }: { status: string }) {
  const label = status.replace(/_/g, " ");
  return (
    <span
      className={`inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[status] ?? "bg-gray-100 text-gray-700"}`}
    >
      {label}
    </span>
  );
}

// ─── Column definitions ─────────────────────────────────────────────────────

const HAULIER_COLUMNS: ColumnDef<HaulierLineItem>[] = [
  { key: "delivery_date", label: "Date", accessor: (r) => r.delivery_date },
  {
    key: "consignment_number",
    label: "Con No",
    accessor: (r) => r.consignment_number,
    renderCell: (row: HaulierLineItem) => (
      <span className="inline-flex items-center gap-1.5">
        {row.consignment_number}
        {row.is_return && (
          <span className="inline-block rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase leading-none text-amber-700">
            RTN
          </span>
        )}
      </span>
    ),
  },
  {
    key: "destination_name",
    label: "Destination",
    accessor: (r) => r.destination_name,
  },
  {
    key: "destination_postcode",
    label: "Postcode",
    accessor: (r) => r.destination_postcode,
  },
  { key: "service_type", label: "Service", accessor: (r) => r.service_type },
  {
    key: "pallet_spec",
    label: "Pallet Size",
    accessor: (r) => r.pallet_spec,
  },
  {
    key: "pallet_quantity",
    label: "Qty",
    accessor: (r) => r.pallet_quantity,
    numeric: true,
  },
  {
    key: "invoiced_amount",
    label: "Invoiced",
    accessor: (r) => r.invoiced_amount,
    numeric: true,
  },
  {
    key: "expected_amount",
    label: "Expected",
    accessor: (r) => r.expected_amount,
    numeric: true,
  },
  {
    key: "amount_difference",
    label: "Difference",
    accessor: (r) => r.amount_difference,
    numeric: true,
  },
  {
    key: "discount_amount",
    label: "Discount",
    accessor: (r) => r.discount_amount,
    numeric: true,
  },
  { key: "status", label: "Status", accessor: (r) => r.status },
];

const WAREHOUSE_COLUMNS: ColumnDef<WarehouseLineItem>[] = [
  { key: "charge_id", label: "ID", accessor: (r) => r.charge_id },
  { key: "created_at", label: "Date", accessor: (r) => r.created_at },
  { key: "charge_type", label: "Type", accessor: (r) => r.charge_type },
  { key: "charge_code", label: "Code", accessor: (r) => r.charge_code },
  { key: "product_code", label: "Product", accessor: (r) => r.product_code },
  {
    key: "storage_unit_reference",
    label: "Storage Unit",
    accessor: (r) => r.storage_unit_reference,
  },
  {
    key: "invoiced_price",
    label: "Invoiced",
    accessor: (r) => r.invoiced_price,
    numeric: true,
  },
  {
    key: "expected_price",
    label: "Expected",
    accessor: (r) => r.expected_price,
    numeric: true,
  },
  {
    key: "price_difference",
    label: "Diff",
    accessor: (r) => r.price_difference,
    numeric: true,
  },
  { key: "status", label: "Status", accessor: (r) => r.status },
];

// ─── Filter predicates ──────────────────────────────────────────────────────

const ISSUE_STATUSES = new Set([
  "OVERCHARGED",
  "UNDERCHARGED",
  "RATE_NOT_FOUND",
  "UNKNOWN_SERVICE",
]);

function matchesFilter(
  row: { status: string; is_return?: boolean },
  filter: Filter,
): boolean {
  switch (filter) {
    case "ALL":
      return true;
    case "ISSUES":
      return ISSUE_STATUSES.has(row.status);
    case "CORRECT":
      return row.status === "CORRECT";
    case "DUPLICATE":
      return row.status === "DUPLICATE";
    case "RATE_NOT_FOUND":
      return row.status === "RATE_NOT_FOUND";
    case "RETURNS":
      return row.is_return === true;
  }
}

// ─── Pagination ─────────────────────────────────────────────────────────────

const PAGE_SIZE = 50;

// ─── Sort icon ──────────────────────────────────────────────────────────────

function SortIcon({
  active,
  dir,
}: {
  active: boolean;
  dir: SortDir;
}) {
  if (!active)
    return (
      <svg
        className="ml-1 inline h-3 w-3 text-gray-400"
        viewBox="0 0 12 12"
        fill="currentColor"
      >
        <path d="M6 1.5l3 4H3zM6 10.5l-3-4h6z" />
      </svg>
    );
  return (
    <svg
      className="ml-1 inline h-3 w-3 text-blue-600"
      viewBox="0 0 12 12"
      fill="currentColor"
    >
      {dir === "asc" ? <path d="M6 2l4 5H2z" /> : <path d="M6 10L2 5h8z" />}
    </svg>
  );
}

// ─── Generic table renderer ─────────────────────────────────────────────────

function Table<T extends { id: string; status: string; is_return?: boolean }>({
  columns,
  data,
  rowClassName,
  showReturns,
}: {
  columns: ColumnDef<T>[];
  data: T[];
  rowClassName?: (row: T) => string;
  showReturns?: boolean;
}) {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [filter, setFilter] = useState<Filter>("ALL");
  const [page, setPage] = useState(0);

  const filtered = useMemo(() => {
    return data.filter((row) => matchesFilter(row, filter));
  }, [data, filter]);

  const sorted = useMemo(() => {
    if (!sortKey) return filtered;
    const col = columns.find((c) => c.key === sortKey);
    if (!col) return filtered;
    return [...filtered].sort((a, b) => {
      const va = col.accessor(a);
      const vb = col.accessor(b);
      if (typeof va === "number" && typeof vb === "number") {
        return sortDir === "asc" ? va - vb : vb - va;
      }
      const sa = String(va);
      const sb = String(vb);
      return sortDir === "asc"
        ? sa.localeCompare(sb)
        : sb.localeCompare(sa);
    });
  }, [filtered, sortKey, sortDir, columns]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const paginated = sorted.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // Reset page when filter changes
  const handleFilter = (f: Filter) => {
    setFilter(f);
    setPage(0);
  };

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const FILTER_TABS: { value: Filter; label: string }[] = [
    { value: "ALL", label: "All" },
    { value: "ISSUES", label: "Issues Only" },
    { value: "CORRECT", label: "Correct" },
    { value: "DUPLICATE", label: "Duplicates" },
    { value: "RATE_NOT_FOUND" as Filter, label: "Rate Not Found" },
    ...(showReturns ? [{ value: "RETURNS" as Filter, label: "Returns" }] : []),
  ];

  return (
    <div>
      {/* Filter tabs */}
      <div className="mb-4 flex gap-2">
        {FILTER_TABS.map((tab) => (
          <button
            key={tab.value}
            onClick={() => handleFilter(tab.value)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              filter === tab.value
                ? "bg-blue-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            {tab.label}
          </button>
        ))}
        <span className="ml-auto self-center text-sm text-gray-500">
          {filtered.length} row{filtered.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  onClick={() => handleSort(col.key)}
                  className={`cursor-pointer select-none whitespace-nowrap px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 hover:text-gray-700 ${
                    col.numeric ? "text-right" : ""
                  }`}
                >
                  {col.label}
                  <SortIcon
                    active={sortKey === col.key}
                    dir={sortDir}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {paginated.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-3 py-8 text-center text-gray-500"
                >
                  No line items match the current filter.
                </td>
              </tr>
            ) : (
              paginated.map((row) => (
                <tr key={row.id} className={`hover:bg-gray-50 ${rowClassName?.(row) ?? ""}`}>
                  {columns.map((col) => {
                    const val = col.accessor(row);
                    if (col.key === "status") {
                      return (
                        <td key={col.key} className="whitespace-nowrap px-3 py-2">
                          <StatusBadge status={String(val)} />
                        </td>
                      );
                    }
                    if (col.renderCell) {
                      return (
                        <td
                          key={col.key}
                          className={`whitespace-nowrap px-3 py-2 ${
                            col.numeric ? "text-right font-mono" : ""
                          }`}
                        >
                          {col.renderCell(row)}
                        </td>
                      );
                    }
                    return (
                      <td
                        key={col.key}
                        className={`whitespace-nowrap px-3 py-2 ${
                          col.numeric ? "text-right font-mono" : ""
                        }`}
                      >
                        {col.numeric && typeof val === "number"
                          ? val.toFixed(2)
                          : val}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-3 flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Page {page + 1} of {totalPages}
          </p>
          <div className="flex gap-2">
            <button
              disabled={page === 0}
              onClick={() => setPage((p) => p - 1)}
              className="rounded-md border border-gray-300 px-3 py-1 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 disabled:opacity-40"
            >
              Previous
            </button>
            <button
              disabled={page >= totalPages - 1}
              onClick={() => setPage((p) => p + 1)}
              className="rounded-md border border-gray-300 px-3 py-1 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Public component ───────────────────────────────────────────────────────

export default function LineItemsTable({
  invoiceType,
  haulierLines,
  warehouseLines,
  showReturns,
}: LineItemsTableProps) {
  if (invoiceType === InvoiceType.HAULIER) {
    return (
      <Table<HaulierLineItem>
        columns={HAULIER_COLUMNS}
        data={haulierLines}
        showReturns={showReturns}
        rowClassName={(row) =>
          row.is_return ? "bg-amber-50 border-l-2 border-l-amber-400" : ""
        }
      />
    );
  }

  return (
    <Table<WarehouseLineItem>
      columns={WAREHOUSE_COLUMNS}
      data={warehouseLines}
    />
  );
}
